import React from 'react';
import type { MobilePreviewBlock } from '../../data/marco2';

/**
 * Static, logic-free miniature of a Mobile screen's layout in BEELYV colours.
 * Pure markup — no contexts, data or screen components are loaded.
 */
export function MobileThumbnail({
  heading,
  blocks
}: {
  heading: string;
  blocks: MobilePreviewBlock[];
}) {
  return <div className="relative aspect-[4/3.6] w-full overflow-hidden bg-sand-deep" aria-hidden="true" data-id="element-931" data-mp="1w8laipoloi2y">
      <div className="absolute bottom-[12px] left-1/2 top-[12px] flex w-[56%] -translate-x-1/2 flex-col overflow-hidden rounded-[16px] border border-sand-line bg-sand shadow-card" data-id="element-932" data-mp="215le8txz98qw">
        <div className="flex items-center gap-[5px] px-[9px] pb-[5px] pt-[9px]" data-id="element-933" data-mp="2awzdr1pxi8k1">
          <span className="h-[7px] w-[7px] rounded-pill bg-gold" data-id="element-934" data-mp="2fvajb096xwwg" />
          <span className="truncate text-[8px] font-bold leading-none text-deep" data-id="element-935" data-mp="2jwmd7hz3l4ag">{heading}</span>
          <span className="ml-auto h-[7px] w-[7px] rounded-pill border border-deep/30" data-id="element-936" data-mp="2jurtu83h5k8h" />
        </div>
        <div className="flex flex-1 flex-col gap-[6px] overflow-hidden px-[9px]" data-id="element-937" data-mp="1sbusthwcsx5a">
          {blocks.map((block, index) => <Block key={`${block}-${index}`} kind={block} data-id="element-938" data-mp="1o9pz6j5wo0in" data-mpi={index} />)}
        </div>
        <div className="flex h-[20px] shrink-0 items-center justify-around bg-deep px-[8px]" data-id="element-939" data-mp="1jawloqqmc9ih">
          {[0, 1, 2, 3, 4].map(item => <span key={item} className={item === 2 ? 'h-[8px] w-[8px] rounded-pill bg-gold' : 'h-[5px] w-[5px] rounded-pill bg-ondark/60'} data-id="element-940" data-mp="1edd7fqtx7wqj" />)}
        </div>
      </div>
    </div>;
}
function Line({
  width,
  tone = 'bg-deep/15'
}: {
  width: string;
  tone?: string;
}) {
  return <span className={`block h-[3px] rounded-pill ${tone}`} style={{
    width
  }} data-id="element-941" data-mp="2286a6maswd6l" />;
}
const barHeights = [45, 70, 55, 85, 60, 90, 50];
const heatLevels = ['bg-sand-line', 'bg-soft/25', 'bg-soft/50', 'bg-soft/80', 'bg-gold'];
function Block({
  kind
}: {
  kind: MobilePreviewBlock;
}) {
  switch (kind) {
    case 'summary':
      return <div className="rounded-[8px] bg-deep p-[6px]" data-id="element-942" data-mp="o2b7e9ttiowx">
          <div className="flex items-end gap-[4px]" data-id="element-943" data-mp="o2upprndrfbh">
            <span className="text-[13px] font-bold leading-none text-gold" data-id="element-944" data-mp="s464f24pug4w">50</span>
            <Line width="30%" tone="bg-sand/30" data-id="element-945" data-mp="echvf7prk7s5" />
          </div>
          <span className="mt-[5px] block h-[2px] rounded-pill bg-sand/15" data-id="element-946" data-mp="ebzsjs8livb9">
            <span className="block h-full w-1/2 rounded-pill bg-gold" data-id="element-947" data-mp="ja1jezc9jyxa" />
          </span>
          <div className="mt-[5px] grid grid-cols-3 gap-[4px]" data-id="element-948" data-mp="4d0qwtm8zzxr">
            {[0, 1, 2].map(item => <Line key={item} width="80%" tone="bg-sand/25" data-id="element-949" data-mp="9dy7x2viunxo" />)}
          </div>
        </div>;
    case 'progress':
      return <div className="rounded-[8px] bg-white p-[6px]" data-id="element-950" data-mp="cguxtdev2d7">
          <div className="flex items-center justify-between" data-id="element-951" data-mp="d9wmfxij1oh">
            <Line width="45%" tone="bg-deep/40" data-id="element-952" data-mp="1pqvgk4t4u94h" />
            <Line width="22%" tone="bg-gold" data-id="element-953" data-mp="1ps6h7vra70rd" />
          </div>
          <span className="mt-[5px] block h-[3px] rounded-pill bg-sand-deep" data-id="element-954" data-mp="1uqrlc69nm4l3">
            <span className="block h-full w-1/2 rounded-pill bg-gold" data-id="element-955" data-mp="24u6l2gr9g90b" />
          </span>
        </div>;
    case 'habits':
      return <div className="flex flex-col" data-id="element-956" data-mp="1zwwpejda3141">
          {[true, true, false, false].map((done, index) => <div key={index} className="flex items-center gap-[5px] border-b border-sand-line py-[4px]" data-id="element-957" data-mp="2dob7mnzd2ya1" data-mpi={index}>
              <span className={`h-[9px] w-[9px] shrink-0 rounded-[3px] ${done ? 'bg-gold' : 'border border-deep/25'}`} data-id="element-958" data-mp="28uqi7kbu3cmg" />
              <Line width={`${50 - index * 5}%`} tone="bg-deep/25" data-id="element-959" data-mp="2nmr2n21ierot" />
              <span className="ml-auto h-[5px] w-[14px] rounded-pill bg-sand-deep" data-id="element-960" data-mp="2ilbdoyw7o115" />
            </div>)}
        </div>;
    case 'cta':
      return <span className="block h-[13px] shrink-0 rounded-pill bg-deep" data-id="element-961" data-mp="2sjhgdipudb72" />;
    case 'bars':
      return <div className="flex h-[26px] shrink-0 items-end gap-[3px]" data-id="element-962" data-mp="1271t8fv3awfl">
          {barHeights.map((height, index) => <span key={index} className={`flex-1 rounded-t-[2px] ${index === barHeights.length - 1 ? 'bg-gold' : 'bg-soft/50'}`} style={{
          height: `${height}%`
        }} data-id="element-963" data-mp="y719qgt4vv1o" data-mpi={index} />)}
        </div>;
    case 'rail':
      return <div className="flex gap-[4px]" data-id="element-964" data-mp="18nleuu55804b">
          {[0, 1, 2, 3, 4].map(item => <span key={item} className={`h-[20px] w-[20px] shrink-0 rounded-[6px] p-[1.5px] ${item === 0 ? 'border border-dashed border-deep/35' : item < 3 ? 'border-[1.5px] border-gold' : 'border-[1.5px] border-sand-line'}`} data-id="element-965" data-mp="1dng6izwqstqf">
              {item > 0 && <span className="block h-full w-full rounded-[4px] bg-soft/70" data-id="element-966" data-mp="px3notzylcl2" />}
            </span>)}
        </div>;
    case 'post':
      return <div className="rounded-[7px] border border-sand-line bg-white p-[5px]" data-id="element-967" data-mp="upwrnfnlsgw3">
          <div className="flex items-center gap-[4px]" data-id="element-968" data-mp="3tbzqp9k86i6y">
            <span className="h-[9px] w-[9px] rounded-pill bg-soft" data-id="element-969" data-mp="26puv19jv9pf" />
            <Line width="35%" tone="bg-deep/30" data-id="element-970" data-mp="3qte57t4nik7w" />
          </div>
          <div className="mt-[4px] flex flex-col gap-[3px]" data-id="element-971" data-mp="3qsuw91wzb660">
            <Line width="90%" data-id="element-972" data-mp="3lu9tovu98aee" />
            <Line width="60%" data-id="element-973" data-mp="3lvcm58786kwf" />
          </div>
          <span className="mt-[5px] block h-[38px] rounded-[5px] bg-soft" data-id="element-974" data-mp="3guxbioebvri5" />
        </div>;
    case 'chips':
      return <div className="flex gap-[4px]" data-id="element-975" data-mp="3ct3cslnp0xkm">
          {[0, 1, 2, 3].map(item => <span key={item} className={`h-[9px] flex-1 rounded-pill ${item === 0 ? 'bg-gold' : 'border border-deep/20'}`} data-id="element-976" data-mp="37v1sjxej6s7k" />)}
        </div>;
    case 'tabs':
      return <div className="flex h-[11px] shrink-0 gap-[2px] rounded-pill bg-sand-deep p-[2px]" data-id="element-977" data-mp="2y5i8iof30sb4">
          <span className="flex-1 rounded-pill bg-deep" data-id="element-978" data-mp="2t7gs6b91j73c" />
          <span className="flex-1" data-id="element-979" data-mp="2p4si608lbu4s" />
          <span className="flex-1" data-id="element-980" data-mp="2p2zdgqhjfjzf" />
        </div>;
    case 'line':
      return <div className="rounded-[8px] bg-white p-[5px]" data-id="element-981" data-mp="2k6imloiyerin">
          <svg viewBox="0 0 100 30" className="block h-[28px] w-full" preserveAspectRatio="none" data-id="element-982" data-mp="2ahljaswdqmm9">
            <polyline points="0,22 16,18 32,20 48,12 64,14 80,8 100,10" fill="none" stroke="var(--bee-gold)" strokeWidth="2" vectorEffect="non-scaling-stroke" data-id="element-983" data-mp="2ej6wyggqn4xi" />
          </svg>
        </div>;
    case 'heatmap':
      return <div className="grid gap-[1.5px]" style={{
        gridTemplateColumns: 'repeat(13, minmax(0, 1fr))'
      }} data-id="element-984" data-mp="2t7jo904vrzlo">
          {Array.from({
          length: 91
        }, (_, index) => <span key={index} className={`aspect-square rounded-[1.5px] ${heatLevels[(index * 7 + index % 5) % 5]}`} data-id="element-985" data-mp="2y98xca8jm374" />)}
        </div>;
    case 'rows':
      return <div className="flex flex-col rounded-[8px] bg-white px-[5px]" data-id="element-986" data-mp="2ofig9qeqwv1g">
          {[0, 1, 2].map(item => <div key={item} className="flex items-center gap-[5px] border-b border-sand-line py-[5px] last:border-0" data-id="element-987" data-mp="3c4umches0mzj">
              <span className="h-[8px] w-[8px] shrink-0 rounded-[3px] bg-sand-deep" data-id="element-988" data-mp="15ioug6le3uku" />
              <Line width={`${55 - item * 8}%`} tone="bg-deep/25" data-id="element-989" data-mp="15gc5h9h7sjya" />
              <span className="ml-auto h-[4px] w-[4px] rotate-45 border-r border-t border-deep/40" data-id="element-990" data-mp="10j2hzzddyid3" />
            </div>)}
        </div>;
    case 'stats':
      return <div className="grid grid-cols-2 gap-[4px]" data-id="element-991" data-mp="1aagf60hv7632">
          {[0, 1].map(item => <div key={item} className="rounded-[7px] bg-white p-[5px]" data-id="element-992" data-mp="mj18xwmyw4mj">
              <Line width="55%" data-id="element-993" data-mp="hkzlz0mf7l5s" />
              <span className={`mt-[4px] block h-[6px] w-[45%] rounded-pill ${item === 0 ? 'bg-gold' : 'bg-deep/60'}`} data-id="element-994" data-mp="hlrgs9871ev0" />
            </div>)}
        </div>;
    case 'hive':
      return <div className="flex justify-center rounded-[8px] bg-deep py-[6px]" data-id="element-995" data-mp="qkmqvwecz93l">
          <svg viewBox="0 0 70 44" className="h-[40px]" data-id="element-996" data-mp="26yv1cslutryv">
            {[[10, 11], [24, 11], [38, 11], [52, 11], [17, 23], [31, 23], [45, 23], [59, 23], [10, 35], [24, 35], [38, 35], [52, 35]].map(([x, y], index) => <polygon key={index} points={hexagon(x, y, 6.5)} fill={index < 6 ? 'var(--bee-gold)' : 'none'} stroke="var(--bee-gold)" strokeOpacity={index < 6 ? 1 : 0.45} strokeWidth="1" data-id="element-997" data-mp="2r1scgv5b29uj" data-mpi={index} />)}
          </svg>
        </div>;
    case 'gate':
      return <div className="grid grid-cols-2 gap-[4px]" data-id="element-998" data-mp="34vjkfz3e1lo5">
          <div className="rounded-[8px] bg-deep p-[6px]" data-id="element-999" data-mp="39r7ashtlwkv8">
            <span className="block h-[9px] w-[9px] rounded-[3px] border border-gold" data-id="element-1000" data-mp="3erd4s4ncmjgh" />
            <span className="mt-[6px] block" data-id="element-1001" data-mp="3epijuqc2urce"><Line width="70%" tone="bg-sand/40" data-id="element-1002" data-mp="3itgikzxhq04x" /></span>
          </div>
          <div className="rounded-[8px] border border-sand-line bg-white p-[6px]" data-id="element-1003" data-mp="3iqlw72ux258v">
            <span className="block h-[9px] w-[9px] rounded-pill border border-gold" data-id="element-1004" data-mp="1kfexi88lnxn8" />
            <span className="mt-[6px] block h-[2px] rounded-pill bg-sand-deep" data-id="element-1005" data-mp="1of78erlgufvr">
              <span className="block h-full w-1/2 rounded-pill bg-gold" data-id="element-1006" data-mp="1gw6d2tktjsjh" />
            </span>
          </div>
        </div>;
    case 'profile':
      return <div className="flex flex-col items-center gap-[4px] py-[2px]" data-id="element-1007" data-mp="175bmxe4gc8sr">
          <span className="h-[26px] w-[26px] rounded-pill border-[1.5px] border-gold p-[2px]" data-id="element-1008" data-mp="1738p47x25bdh">
            <span className="block h-full w-full rounded-pill bg-soft" data-id="element-1009" data-mp="131wyq06d0m48" />
          </span>
          <Line width="40%" tone="bg-deep/40" data-id="element-1010" data-mp="1zth79zrlfvs2" />
          <div className="flex w-full justify-around" data-id="element-1011" data-mp="1uuny2jjcaqxp">
            {[0, 1, 2].map(item => <Line key={item} width="18%" data-id="element-1012" data-mp="1qt2l6y6t2cni" />)}
          </div>
        </div>;
    case 'days':
      return <div className="rounded-[8px] bg-white p-[6px]" data-id="element-1013" data-mp="1ltpntjib5n6o">
          <div className="flex justify-between" data-id="element-1014" data-mp="fe5kus56810u">
            {['done', 'done', 'done', 'protected', 'done', 'done', 'open'].map((state, index) => <span key={index} className={`h-[11px] w-[11px] rounded-pill ${state === 'done' ? 'bg-soft' : state === 'protected' ? 'border-[1.5px] border-dashed border-gold' : 'border border-deep/25'}`} data-id="element-1015" data-mp="adrbn6ru2l18" data-mpi={index} />)}
          </div>
        </div>;
    case 'ring':
      return <div className="flex justify-center py-[2px]" data-id="element-1016" data-mp="qb3mvq6qyyo6">
          <svg viewBox="0 0 40 40" className="h-[50px] w-[50px]" data-id="element-1017" data-mp="q9iuo77wnrth">
            <circle cx="20" cy="20" r="16" fill="none" stroke="var(--bee-line)" strokeWidth="3" data-id="element-1018" data-mp="14yoqrmbg51x5" />
            <circle cx="20" cy="20" r="16" fill="none" stroke="var(--bee-gold)" strokeWidth="3" strokeDasharray="50 100" strokeLinecap="round" transform="rotate(-90 20 20)" data-id="element-1019" data-mp="100djnjcjd5im" />
            <text x="20" y="24" textAnchor="middle" fontSize="11" fontWeight="700" fill="var(--bee-deep)" data-id="element-1020" data-mp="3fskwkpxn3cct">
              50
            </text>
          </svg>
        </div>;
    case 'mood':
      return <div className="flex justify-between rounded-[8px] bg-white p-[6px]" data-id="element-1021" data-mp="31ovmkql7jvcx">
          {[0, 1, 2, 3, 4].map(item => <span key={item} className={`h-[12px] w-[12px] rounded-pill ${item === 3 ? 'bg-gold' : 'border border-deep/25'}`} data-id="element-1022" data-mp="2rx7q2b9sno4l" />)}
        </div>;
    default:
      return null;
  }
}
function hexagon(cx: number, cy: number, r: number) {
  return Array.from({
    length: 6
  }, (_, index) => {
    const angle = Math.PI / 3 * index + Math.PI / 6;
    return `${(cx + r * Math.cos(angle)).toFixed(2)},${(cy + r * Math.sin(angle)).toFixed(2)}`;
  }).join(' ');
}